import React, { useEffect, useState } from 'react';

import moment from 'moment-timezone';

import type { DateRange, Sort, SortOrder, UUID } from '@cobliteam/common-types';

import { type Checklist, isWebDataFetching, isWebDataSuccess, type NewChecklist, type UpdatedChecklist, type WebData } from '@cobliteam/api-lib';
import type { ChecklistsNames, ChecklistsPage as ChecklistsPageType, TemplateChecklist } from '@cobliteam/api-lib/src/dashboard/checklist/Definitions/Types';

import { Analytics } from '@cobliteam/analytics';
import { CookieManager } from '@cobliteam/cookie-manager';

import { useHubspot } from '@cobliteam/hooks';

import { type CheckboxOption, useDebounce } from '@cobliteam/base-components';

import { Page } from '@cobliteam/ui/components';

import { TabPanelOptions } from '../../constants';
import { ChecklistDrawer } from '../ChecklistDrawer/ChecklistDrawer';
import { DRAWER_WIDTH } from '../ChecklistDrawer/styles';
import { ChecklistReceivedDrawer } from '../ChecklistReceivedDrawer/ChecklistReceivedDrawer';
import ChecklistReceivedTableFilter from '../ChecklistReceivedTableFilter/ChecklistReceivedTableFilter';
import { ChecklistsHeader } from '../ChecklistsHeader/ChecklistsHeader';
import { ChecklistsTable } from '../ChecklistsTable/ChecklistsTable';
import { createdListColumns, receivedListColumns } from '../ChecklistsTable/columns';
import { DeleteChecklistDialog } from '../DeleteChecklistDialog/DeleteChecklistDialog';
import { EmptyState } from '../EmptyState/EmptyState';
import { NewChecklistDialog } from '../NewChecklistDialog/NewChecklistDialog';
import { PublishChecklistDialog, REMINDER_COOKIE_NAME } from '../PublishChecklistDialog/PublishChecklistDialog';
import StepByStepEmptyState from '../StepByStepEmptyState/StepByStepEmptyState';

const DEBOUNCE_TIME = 300;
const DEFAULT_SELECTED_PERIOD_WEEKS = 1;
const DEFAULT_CHECKLISTS_PER_PAGE = 25;
const DEFAULT_PAGE = 0;
export interface ChecklistsPageProps {
  templateChecklistsWebData: WebData<ChecklistsPageType>;
  completedChecklistsWebData: WebData<ChecklistsPageType>;
  completedChecklistsNamesWebData: WebData<ChecklistsNames>;
  loadCompletedChecklists: (period?: DateRange, page?: number, size?: number, sort?: Sort, inputFilter?: string, nameFilters?: string[]) => void;
  loadTemplateChecklists: () => void;
  loadCompletedChecklistsNames: () => void;
  createChecklist: (createdChecklist: NewChecklist) => void;
  updateChecklist: (id: UUID, updatedChecklist: UpdatedChecklist) => void;
  deleteChecklist: (deletedChecklist: Checklist) => void;
  saveChecklistWebData: WebData<void>;
  deleteChecklistWebData: WebData<void>;
}

export const ChecklistsPage: React.FC<ChecklistsPageProps> = ({
  templateChecklistsWebData,
  completedChecklistsWebData,
  completedChecklistsNamesWebData,
  loadCompletedChecklists,
  loadTemplateChecklists,
  loadCompletedChecklistsNames,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  deleteChecklistWebData,
  saveChecklistWebData,
}) => {
  const [selectedPanel, setSelectedPanel] = useState(TabPanelOptions.RECEIVED_LISTS);
  const [openTemplateChecklistDrawer, setOpenTemplateChecklistDrawer] = useState(false);
  const [openReceivedChecklistDrawer, setOpenReceivedChecklistDrawer] = useState(false);
  const [initialChecklistName, setInitialChecklistName] = useState('');
  const [openNewChecklistModal, setOpenNewChecklistModal] = useState(false);
  const [openDeleteChecklistModal, setOpenDeleteChecklistModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | undefined>(undefined);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [checklistToPublish, setChecklistToPublish] = useState<TemplateChecklist | undefined>(undefined);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [checklistsPerPage, setChecklistsPerPage] = useState(DEFAULT_CHECKLISTS_PER_PAGE);
  const [sortBy, setsortBy] = useState<Sort | undefined>(undefined);
  const [inputFilter, setInputFilter] = useState('');
  const [templateChecklistNames, setTemplateChecklistNames] = useState<Map<string, string>>(new Map<string, string>());
  const [chipFilter, setChipFilter] = React.useState<CheckboxOption[]>([]);
  const [period, setPeriod] = React.useState<DateRange>({
    start: moment().startOf('day').subtract(DEFAULT_SELECTED_PERIOD_WEEKS, 'week').valueOf(),
    end: moment.utc().valueOf(),
  });

  const hubspot = useHubspot();

  const debouncedInputtedFilter = useDebounce(inputFilter, DEBOUNCE_TIME);

  useEffect(() => {
    if (openTemplateChecklistDrawer || openReceivedChecklistDrawer) {
      hubspot.moveToLeft(DRAWER_WIDTH);
    }
  }, [openTemplateChecklistDrawer, openReceivedChecklistDrawer]);

  const handleOpenNewChecklistModal = () => {
    Analytics.track('New Checklist Modal Opened');

    setOpenNewChecklistModal(true);
  };

  const renderChecklistsTable = () => {
    const defaultProps = {
      emptyState: (
        <EmptyState
          onClick={handleOpenNewChecklistModal}
          selectedPanel={selectedPanel}
          hasFilteredChecklists={isWebDataSuccess(completedChecklistsNamesWebData) && completedChecklistsNamesWebData.data.names.length > 0}
        />
      ),
    };

    switch (selectedPanel) {
      case TabPanelOptions.RECEIVED_LISTS:
        return (
          <>
            <ChecklistReceivedTableFilter
              chipFilter={chipFilter}
              setChipFilter={setChipFilter}
              period={period}
              setPeriod={setPeriod}
              onInputFilterChange={setInputFilter}
              checklistsNamesWebData={completedChecklistsNamesWebData}
            />
            <ChecklistsTable
              {...defaultProps}
              checklistsPageWebData={completedChecklistsWebData}
              sortBy={sortBy}
              onEdit={handleOnEdit(selectedPanel)}
              selectedPanel={selectedPanel}
              columns={receivedListColumns()}
              onPaginationChange={handlePaginationChange}
              onRequestSort={handleRequestSort}
              backendPagination
            />
            {selectedChecklist && <ChecklistReceivedDrawer selectedChecklist={selectedChecklist} open={openReceivedChecklistDrawer} onCloseDrawer={handleCloseDrawers} />}
          </>
        );

      default:
        return (
          <ChecklistsTable
            {...defaultProps}
            checklistsPageWebData={templateChecklistsWebData}
            onEdit={handleOnEdit(selectedPanel)}
            selectedPanel={selectedPanel}
            columns={createdListColumns()}
          />
        );
    }
  };

  const handleOnEdit = (panel: TabPanelOptions) => (row: Checklist) => {
    Analytics.track('Checklist Drawer Opened', {
      checklistId: row.id,
      checklistType: panel,
    });

    setSelectedChecklist(row);

    if (panel === TabPanelOptions.CREATED_LISTS) {
      return setOpenTemplateChecklistDrawer(true);
    }

    return setOpenReceivedChecklistDrawer(true);
  };

  const handleOnPublishModalConfirm = (id?: UUID) => () => {
    if (checklistToPublish) {
      handleOnSubmitChecklist(checklistToPublish, id);
    }
  };

  const handleOnSubmitFormClickButton = (id?: UUID) => (checklist: TemplateChecklist) => {
    if (!CookieManager.getCookie(REMINDER_COOKIE_NAME) && selectedChecklist) {
      setChecklistToPublish(checklist);
      setOpenPublishModal(true);
    } else {
      handleOnSubmitChecklist(checklist, id);
    }
  };

  const handleOnSubmitChecklist = (checklist: TemplateChecklist, id?: UUID) => {
    if (id) {
      updateChecklist(id, checklist);
    } else if ('template' in checklist) {
      createChecklist(checklist);
    }

    const eventDescription = 'template' in checklist ? 'New Checklist Created' : 'Checklist Updated';

    Analytics.track(eventDescription, {
      checklistName: checklist.name,
    });
  };

  const handleOnDeleteChecklist = (target: Checklist) => () => {
    Analytics.track('Delete Checklist Clicked', {
      checklistId: target.id,
    });

    deleteChecklist(target);
  };

  const handleOnCancelNewChecklistModal = () => {
    setOpenNewChecklistModal(false);
  };

  const handleOnCancelPublishChecklistModal = () => {
    setOpenPublishModal(false);
  };

  const handleOnConfirmNewChecklistModal = (name: string) => {
    setOpenNewChecklistModal(false);
    setInitialChecklistName(name);
    setSelectedChecklist(undefined);
    setOpenTemplateChecklistDrawer(true);
  };

  const handleCloseDrawers = () => {
    setOpenReceivedChecklistDrawer(false);
    setOpenTemplateChecklistDrawer(false);
    setOpenPublishModal(false);
    setOpenDeleteChecklistModal(false);
    setInitialChecklistName('');
    hubspot.moveToRight();
  };

  const handleSelectedPanelChange = (panel: TabPanelOptions) => {
    Analytics.track('Checklists Panel Changed', {
      checklistsPanel: panel,
    });

    setSelectedPanel(panel);
  };

  const handlePaginationChange = (newPage: number, newRowsPerPage: number) => {
    setPage(newPage);
    setChecklistsPerPage(newRowsPerPage);
  };

  const handleRequestSort = (column: string, order: SortOrder) => {
    setsortBy({ column, order });
  };

  useEffect(() => {
    loadCompletedChecklistsNames();
    loadCompletedChecklists(period, page, checklistsPerPage);
    loadTemplateChecklists();
  }, []);

  useEffect(() => {
    const chipFilterString = chipFilter.map((chip) => chip.label);
    setPage(DEFAULT_PAGE);
    loadCompletedChecklists(period, DEFAULT_PAGE, checklistsPerPage, sortBy, debouncedInputtedFilter, chipFilterString);
  }, [period, checklistsPerPage, sortBy, debouncedInputtedFilter, chipFilter]);

  useEffect(() => {
    const chipFilterString = chipFilter.map((chip) => chip.label);
    loadCompletedChecklists(period, page, checklistsPerPage, sortBy, debouncedInputtedFilter, chipFilterString);
  }, [page]);

  useEffect(() => {
    if (isWebDataSuccess(templateChecklistsWebData)) {
      const checklistNamesMap = new Map<string, string>();
      templateChecklistsWebData.data.page.content.map((checklist) => checklistNamesMap.set(checklist.name.toLowerCase(), checklist.id));
      setTemplateChecklistNames(checklistNamesMap);
    }

    if (isWebDataSuccess(completedChecklistsWebData)) {
      handleCloseDrawers();
    }
  }, [templateChecklistsWebData, completedChecklistsWebData]);

  const isTheFirstTimeUsingChecklists =
    isWebDataSuccess(templateChecklistsWebData) &&
    isWebDataSuccess(completedChecklistsWebData) &&
    !templateChecklistsWebData.data.page.content.length &&
    !completedChecklistsWebData.data.page.content.length &&
    !initialChecklistName;
  if (isTheFirstTimeUsingChecklists) {
    return (
      <Page>
        <ChecklistsHeader onButtonClick={handleOpenNewChecklistModal} isEmptyState />

        <Page.Body>
          <StepByStepEmptyState onCreateNewChecklist={handleOpenNewChecklistModal} />
          <NewChecklistDialog
            open={openNewChecklistModal}
            onCancel={handleOnCancelNewChecklistModal}
            checklistNames={templateChecklistNames}
            onConfirm={handleOnConfirmNewChecklistModal}
          />
        </Page.Body>
      </Page>
    );
  }

  return (
    <Page>
      <ChecklistsHeader selectedPanel={selectedPanel} onButtonClick={handleOpenNewChecklistModal} onTabPanelClick={handleSelectedPanelChange} />

      <Page.Body>
        {renderChecklistsTable()}
        <ChecklistDrawer
          name={selectedChecklist?.name || initialChecklistName}
          checklistNames={templateChecklistNames}
          open={openTemplateChecklistDrawer}
          hasLoadingRequests={isWebDataFetching(saveChecklistWebData)}
          onCloseDrawer={handleCloseDrawers}
          handleOnSubmit={handleOnSubmitFormClickButton(selectedChecklist?.id)}
          onDeleteClickButton={() => setOpenDeleteChecklistModal(true)}
          checklist={selectedChecklist}
        />
        <NewChecklistDialog
          open={openNewChecklistModal}
          onCancel={handleOnCancelNewChecklistModal}
          checklistNames={templateChecklistNames}
          onConfirm={handleOnConfirmNewChecklistModal}
        />
        <PublishChecklistDialog
          open={openPublishModal}
          loading={isWebDataFetching(saveChecklistWebData)}
          onCancel={handleOnCancelPublishChecklistModal}
          onConfirm={handleOnPublishModalConfirm(selectedChecklist?.id)}
        />
        {selectedChecklist && (
          <DeleteChecklistDialog
            open={openDeleteChecklistModal}
            checklistName={selectedChecklist.name}
            onCancel={() => setOpenDeleteChecklistModal(false)}
            onConfirm={handleOnDeleteChecklist(selectedChecklist)}
            loading={isWebDataFetching(deleteChecklistWebData)}
          />
        )}
      </Page.Body>
    </Page>
  );
};

export default ChecklistsPage;
